import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { getUsers, createUser, updateUser, deleteUser, getAllVerticalsAdmin, searchLeads } from '../../lib/api';
import AdminLayout from '../../components/AdminLayout';
import { 
  Users, 
  Plus, 
  Pencil, 
  Trash2, 
  X, 
  Mail, 
  Lock, 
  User,
  Shield,
  Building2,
  Loader2,
  Save,
  ExternalLink,
  Search,
  Link2
} from 'lucide-react';

export default function AdminUsers() {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'user',
    assigned_verticals: [],
    user_type: 'internal',
    linked_lead_id: null,
    can_access_collections: false,
    can_access_presales: false
  });
  const [isSaving, setIsSaving] = useState(false);
  const [leadSearchQuery, setLeadSearchQuery] = useState('');
  const [leadSearchResults, setLeadSearchResults] = useState([]);
  const [isSearchingLeads, setIsSearchingLeads] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [resetPasswordModal, setResetPasswordModal] = useState(null);
  const [resetNewPassword, setResetNewPassword] = useState('');

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [usersRes, verticalsRes] = await Promise.all([
        getUsers(),
        getAllVerticalsAdmin()
      ]);
      setUsers(usersRes.data);
      setVerticals(verticalsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        password: '',
        name: user.name || '',
        role: user.role,
        assigned_verticals: user.assigned_verticals || [],
        user_type: user.user_type || 'internal',
        linked_lead_id: user.linked_lead_id || null,
        can_access_collections: !!user.can_access_collections,
        can_access_presales: !!user.can_access_presales,
      });
      if (user.linked_lead_id) {
        // Try to find the lead info
        setSelectedLead({ id: user.linked_lead_id });
      } else {
        setSelectedLead(null);
      }
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        password: '',
        name: '',
        role: 'user',
        assigned_verticals: [],
        user_type: 'internal',
        linked_lead_id: null,
        can_access_collections: false,
        can_access_presales: false,
      });
      setSelectedLead(null);
    }
    setLeadSearchQuery('');
    setLeadSearchResults([]);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      email: '',
      password: '',
      name: '',
      role: 'user',
      assigned_verticals: [],
      user_type: 'internal',
      linked_lead_id: null,
      can_access_collections: false,
      can_access_presales: false,
    });
    setLeadSearchQuery('');
    setLeadSearchResults([]);
    setSelectedLead(null);
  };

  const handleVerticalToggle = (verticalId) => {
    setFormData(prev => ({
      ...prev,
      assigned_verticals: prev.assigned_verticals.includes(verticalId)
        ? prev.assigned_verticals.filter(id => id !== verticalId)
        : [...prev.assigned_verticals, verticalId]
    }));
  };

  const handleLeadSearch = async (query) => {
    setLeadSearchQuery(query);
    if (query.length < 2) {
      setLeadSearchResults([]);
      return;
    }
    
    setIsSearchingLeads(true);
    try {
      const response = await searchLeads(query);
      setLeadSearchResults(response.data);
    } catch (error) {
      toast.error("Failed to search leads");
    } finally {
      setIsSearchingLeads(false);
    }
  };

  const handleSelectLead = (lead) => {
    setSelectedLead(lead);
    setFormData(prev => ({
      ...prev,
      linked_lead_id: lead.id
    }));
    setLeadSearchResults([]);
    setLeadSearchQuery('');
  };

  const handleRemoveLead = () => {
    setSelectedLead(null);
    setFormData(prev => ({
      ...prev,
      linked_lead_id: null
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email) {
      toast.error('Email is required');
      return;
    }
    
    if (!editingUser && !formData.password) {
      toast.error('Password is required for new users');
      return;
    }

    setIsSaving(true);
    try {
      if (editingUser) {
        // Update existing user
        const updateData = {
          email: formData.email,
          name: formData.name,
          assigned_verticals: formData.assigned_verticals,
          user_type: formData.user_type,
          linked_lead_id: formData.user_type === 'external' ? formData.linked_lead_id : null,
          can_access_collections: !!formData.can_access_collections,
          can_access_presales: !!formData.can_access_presales,
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await updateUser(editingUser.id, updateData);
        toast.success('User updated successfully');
      } else {
        // Create new user
        const createData = {
          ...formData,
          linked_lead_id: formData.user_type === 'external' ? formData.linked_lead_id : null
        };
        await createUser(createData);
        toast.success('User created successfully');
      }
      handleCloseModal();
      loadData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Operation failed';
      toast.error(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await deleteUser(userId);
      toast.success('User deleted successfully');
      loadData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to delete user';
      toast.error(errorMsg);
    }
  };

  const handleAdminResetPassword = async () => {
    if (!resetPasswordModal || !resetNewPassword) return;
    if (resetNewPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    try {
      const api = (await import('../../lib/api')).default;
      await api.post(`/auth/admin-reset-password/${resetPasswordModal.id}`, { new_password: resetNewPassword });
      toast.success(`Password reset for ${resetPasswordModal.email}`);
      setResetPasswordModal(null);
      setResetNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const getVerticalNames = (verticalIds) => {
    if (!verticalIds || verticalIds.length === 0) return 'All Verticals';
    const names = verticalIds
      .map(id => verticals.find(v => v.id === id)?.name)
      .filter(Boolean);
    return names.length > 0 ? names.join(', ') : 'None assigned';
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">{t("admin.users.title")}</h1>
            <p className="text-slate-500 mt-1">{t("admin.users.subtitle")}</p>
          </div>
          <Button 
            onClick={() => handleOpenModal()} 
            className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
            data-testid="add-user-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t("admin.users.addUser")}
          </Button>
        </div>

        {/* Users List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : users.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-semibold text-slate-700">{t("admin.users.noUsersYet")}</h3>
              <p className="text-slate-500 text-sm mt-1">{t("admin.users.createFirstUser")}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {users.map((user) => (
              <Card key={user.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        user.role === 'admin' 
                          ? 'bg-gradient-to-r from-purple-500 to-pink-500' 
                          : user.user_type === 'external'
                          ? 'bg-gradient-to-r from-orange-500 to-amber-500'
                          : 'bg-gradient-to-r from-blue-500 to-cyan-500'
                      }`}>
                        {user.role === 'admin' 
                          ? <Shield className="w-6 h-6 text-white" />
                          : user.user_type === 'external'
                          ? <ExternalLink className="w-6 h-6 text-white" />
                          : <User className="w-6 h-6 text-white" />
                        }
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-800">{user.name || user.email}</h3>
                        <p className="text-sm text-slate-500">{user.email}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            user.role === 'admin'
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {user.role === 'admin' ? t("admin.users.admin") : t("admin.users.user")}
                          </span>
                          {user.role !== 'admin' && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              user.user_type === 'external'
                                ? 'bg-orange-100 text-orange-700'
                                : 'bg-green-100 text-green-700'
                            }`}>
                              {user.user_type === 'external' ? t("admin.users.external") : t("admin.users.internal")}
                            </span>
                          )}
                          {user.linked_lead_id && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 flex items-center gap-1">
                              <Link2 className="w-3 h-3" />
                              {t("admin.users.linkedToLead")}
                            </span>
                          )}
                          {user.role === 'user' && (
                            <span className="text-xs text-slate-400">
                              {getVerticalNames(user.assigned_verticals)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenModal(user)}
                        className="hover:bg-blue-50 hover:text-blue-600"
                        data-testid={`edit-user-${user.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => { setResetPasswordModal(user); setResetNewPassword(''); }}
                        className="hover:bg-amber-50 hover:text-amber-600"
                        title="Reset Password"
                        data-testid={`reset-password-${user.id}`}
                      >
                        <Lock className="w-4 h-4" />
                      </Button>
                      {user.role !== 'admin' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(user.id)}
                          className="hover:bg-red-50 hover:text-red-600"
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <CardHeader className="border-b">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{editingUser ? t("admin.users.editUser") : t("admin.users.createUser")}</CardTitle>
                    <CardDescription>
                      {editingUser ? t("admin.users.updateUserDetails") : t("admin.users.addNewUser")}
                    </CardDescription>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={handleCloseModal}
                    data-testid="close-user-modal"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="name">{t("admin.users.name")}</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="name"
                        placeholder="Full name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="pl-10"
                        data-testid="user-name-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">{t("admin.users.email")} *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="user@company.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="pl-10"
                        required
                        data-testid="user-email-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">
                      {t("admin.users.password")} {editingUser ? `(${t("admin.users.leaveBlank")})` : '*'}
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="password"
                        type="password"
                        placeholder={editingUser ? '••••••••' : 'Enter password'}
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="pl-10"
                        required={!editingUser}
                        data-testid="user-password-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>{t("admin.users.role")}</Label>
                    <Select
                      value={formData.role}
                      onValueChange={(value) => setFormData({ ...formData, role: value })}
                      disabled={editingUser?.role === 'admin'}
                    >
                      <SelectTrigger data-testid="user-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">{t("admin.users.user")}</SelectItem>
                        <SelectItem value="admin">{t("admin.users.admin")}</SelectItem>
                      </SelectContent>
                    </Select>
                    {editingUser?.role === 'admin' && (
                      <p className="text-xs text-slate-500">{t("admin.users.adminRoleNote")}</p>
                    )}
                  </div>

                  {formData.role === 'user' && (
                    <>
                      {/* User Type Selection */}
                      <div className="space-y-2">
                        <Label>{t("admin.users.userType")}</Label>
                        <Select
                          value={formData.user_type}
                          onValueChange={(value) => {
                            setFormData({ ...formData, user_type: value });
                            if (value === 'internal') {
                              setSelectedLead(null);
                              setFormData(prev => ({ ...prev, linked_lead_id: null }));
                            }
                          }}
                        >
                          <SelectTrigger data-testid="user-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="internal">
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4" />
                                <span>{t("admin.users.internalEmployee")}</span>
                              </div>
                            </SelectItem>
                            <SelectItem value="external">
                              <div className="flex items-center gap-2">
                                <ExternalLink className="w-4 h-4" />
                                <span>{t("admin.users.externalCustomer")}</span>
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Module Access Flags */}
                      <div className="border rounded-lg p-3 bg-slate-50/60 space-y-2" data-testid="user-access-flags">
                        <p className="text-xs font-semibold text-slate-700">Module Access</p>
                        <p className="text-[10px] text-slate-500">Grant this non-admin user access to specific modules. Admin users automatically get access to everything.</p>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!formData.can_access_collections}
                            onChange={e => setFormData({ ...formData, can_access_collections: e.target.checked })}
                            data-testid="user-flag-collections"
                            className="rounded border-slate-300"
                          />
                          <span className="text-xs">Can access <strong>Collections</strong></span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!formData.can_access_presales}
                            onChange={e => setFormData({ ...formData, can_access_presales: e.target.checked })}
                            data-testid="user-flag-presales"
                            className="rounded border-slate-300"
                          />
                          <span className="text-xs">Can access <strong>Pre-Sales Hub</strong></span>
                        </label>
                      </div>

                      {/* Link to Lead (only for external users) */}
                      {formData.user_type === 'external' && (
                        <div className="space-y-2">
                          <Label>{t("admin.users.linkToLead")}</Label>
                          <p className="text-xs text-slate-500 mb-2">
                            {t("admin.users.searchLeadsHint")}
                          </p>
                          
                          {selectedLead ? (
                            <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                              <Link2 className="w-4 h-4 text-amber-600" />
                              <div className="flex-1">
                                <p className="text-sm font-medium text-amber-800">{selectedLead.name || 'Lead'}</p>
                                <p className="text-xs text-amber-600">{selectedLead.email} • {selectedLead.company}</p>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={handleRemoveLead}
                                className="text-amber-600 hover:text-amber-800 hover:bg-amber-100"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <Input
                                  placeholder="Search leads by email or company..."
                                  value={leadSearchQuery}
                                  onChange={(e) => handleLeadSearch(e.target.value)}
                                  className="pl-10"
                                  data-testid="lead-search-input"
                                />
                                {isSearchingLeads && (
                                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />
                                )}
                              </div>
                              
                              {leadSearchResults.length > 0 && (
                                <div className="border rounded-lg divide-y max-h-40 overflow-y-auto">
                                  {leadSearchResults.map((lead) => (
                                    <button
                                      key={lead.id}
                                      type="button"
                                      onClick={() => handleSelectLead(lead)}
                                      className="w-full p-3 text-left hover:bg-slate-50 flex items-center gap-3"
                                    >
                                      <Building2 className="w-4 h-4 text-slate-400" />
                                      <div>
                                        <p className="text-sm font-medium text-slate-800">{lead.name}</p>
                                        <p className="text-xs text-slate-500">{lead.email} • {lead.company}</p>
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      <div className="space-y-3">
                        <Label>{t("admin.users.assignedVerticals")}</Label>
                        <p className="text-xs text-slate-500">
                          {t("admin.users.assignedVerticalsHint")}
                        </p>
                        <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                          {verticals.map((vertical) => (
                            <label
                              key={vertical.id}
                              className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer"
                            >
                              <Checkbox
                                checked={formData.assigned_verticals.includes(vertical.id)}
                                onCheckedChange={() => handleVerticalToggle(vertical.id)}
                                data-testid={`vertical-checkbox-${vertical.id}`}
                              />
                              <span className="text-sm truncate">{vertical.name}</span>
                            </label>
                          ))}
                          {verticals.length === 0 && (
                            <p className="text-sm text-slate-400 col-span-2 text-center py-4">
                              {t("admin.users.noVerticalsAvailable")}
                            </p>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={handleCloseModal}
                      data-testid="cancel-user-btn"
                    >
                      {t("common.cancel")}
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={isSaving}
                      className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
                      data-testid="save-user-btn"
                    >
                      {isSaving ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t("admin.common.saving")}
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          {editingUser ? t("admin.users.editUser") : t("admin.users.createUser")}
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Admin Reset Password Modal */}
      {resetPasswordModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-sm">
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Reset Password</CardTitle>
                  <CardDescription>For {resetPasswordModal.email}</CardDescription>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setResetPasswordModal(null)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <div className="space-y-2">
                <Label>New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="password"
                    placeholder="Min 6 characters"
                    value={resetNewPassword}
                    onChange={(e) => setResetNewPassword(e.target.value)}
                    className="pl-9"
                    data-testid="admin-reset-password-input"
                  />
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setResetPasswordModal(null)}>Cancel</Button>
                <Button
                  onClick={handleAdminResetPassword}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                  data-testid="admin-reset-password-confirm"
                >
                  <Lock className="w-4 h-4 mr-2" /> Reset Password
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </AdminLayout>
  );
}
